import React, { useMemo } from "react";
import { Card } from "@shadcn-in-nx/ui/card";
import { Button } from "@shadcn-in-nx/ui/button";
import { Badge } from "@shadcn-in-nx/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@shadcn-in-nx/ui/avatar";
import {
    MessageSquarePlus,
    ChevronRight,
    Clock,
    AlertCircle,
    CheckCircle2,
    Loader2,
    Inbox,
    Headphones,
    Eye,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useTranslation } from "react-i18next";

const statusConfig = {
    OPEN: {
        label: "Open",
        color: "bg-blue-100 text-blue-800 border-blue-200",
        icon: AlertCircle,
    },
    IN_PROGRESS: {
        label: "In Progress",
        color: "bg-yellow-100 text-yellow-800 border-yellow-200",
        icon: Clock,
    },
    WAITING_ON_CUSTOMER: {
        label: "Awaiting Reply",
        color: "bg-orange-100 text-orange-800 border-orange-200",
        icon: Clock,
    },
    RESOLVED: {
        label: "Resolved",
        color: "bg-green-100 text-green-800 border-green-200",
        icon: CheckCircle2,
    },
    CLOSED: {
        label: "Closed",
        color: "bg-gray-100 text-gray-800 border-gray-200",
        icon: CheckCircle2,
    },
};

const priorityConfig = {
    LOW: { label: "Low", color: "text-gray-500" },
    NORMAL: { label: "Normal", color: "text-blue-500" },
    HIGH: { label: "High", color: "text-orange-500" },
    URGENT: { label: "Urgent", color: "text-red-500" },
};

export default function TicketList({
    tickets = [],
    isLoading,
    onSelectTicket,
    onCreateTicket,
}) {
    const { t } = useTranslation();

    const { ticketViews, lastViewedId } = useMemo(() => {
        try {
            return {
                ticketViews: JSON.parse(localStorage.getItem("support_ticket_views") || "{}"),
                lastViewedId: localStorage.getItem("support_last_viewed_ticket"),
            };
        } catch { return { ticketViews: {}, lastViewedId: null }; }
    }, [tickets]);

    // Helper to check if sender is staff
    const isStaffSender = (sender) => {
        return sender?.roles?.some(
            r => r === "admin" || r === "super-admin" || r === "support"
        );
    };

    // Get initials from name
    const getInitials = (firstName, lastName) => {
        const first = firstName?.charAt(0)?.toUpperCase() || "";
        const last = lastName?.charAt(0)?.toUpperCase() || "";
        return first + last || "?";
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
        );
    }

    if (tickets.length === 0) {
        return (
            <Card className="p-8 md:p-12 text-center border-2 border-dashed border-border/50">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
                        <Inbox className="w-8 h-8 text-muted-foreground" />
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold mb-2">No tickets yet</h3>
                        <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                            Need help? Create a support ticket and our team will get back to you
                            as soon as possible.
                        </p>
                        <Button
                            onClick={onCreateTicket}
                            className="bg-gray-900 hover:bg-gray-800 text-white"
                        >
                            <MessageSquarePlus className="w-4 h-4 mr-2" />
                            Create Your First Ticket
                        </Button>
                    </div>
                </div>
            </Card>
        );
    }

    return (
        <div className="space-y-3">
            {tickets.map((ticket) => {
                const status = statusConfig[ticket.status] || statusConfig.OPEN;
                const priority = priorityConfig[ticket.priority] || priorityConfig.NORMAL;
                const StatusIcon = status.icon;
                const lastMessage = ticket.lastMessage;
                const lastSender = lastMessage?.sender;
                const isStaff = isStaffSender(lastSender);
                const isLastViewed = ticket.id === lastViewedId;
                const lastViewedAt = ticketViews[ticket.id];

                return (
                    <Card
                        key={ticket.id}
                        className={`p-4 md:p-5 border-2 hover:border-border transition-colors cursor-pointer group ${isLastViewed ? "ring-2 ring-blue-400 bg-blue-50/50 border-border/50" : lastViewedAt ? "border-l-4 border-l-blue-400 border-border/50" : "border-border/50"}`}
                        onClick={() => onSelectTicket(ticket.id)}
                    >
                        <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-2">
                                    <Badge
                                        variant="outline"
                                        className={`${status.color} text-xs font-medium`}
                                    >
                                        <StatusIcon className="w-3 h-3 mr-1" />
                                        {status.label}
                                    </Badge>
                                    {ticket.priority && ticket.priority !== "NORMAL" && (
                                        <span className={`text-xs font-medium ${priority.color}`}>
                                            {priority.label}
                                        </span>
                                    )}
                                </div>
                                <h3 className="font-semibold text-foreground mb-1 truncate group-hover:text-gray-700 transition-colors">
                                    {ticket.subject}
                                </h3>
                                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                    <span>#{ticket.id.slice(-8)}</span>
                                    <span>
                                        {formatDistanceToNow(new Date(ticket.createdAt), {
                                            addSuffix: true,
                                        })}
                                    </span>
                                    {ticket.messageCount > 0 && (
                                        <span>
                                            {ticket.messageCount} {ticket.messageCount !== 1 ? t("support.tickets.messages") : t("support.tickets.message")}
                                        </span>
                                    )}
                                    {lastViewedAt && (
                                        <span className="flex items-center gap-1 text-blue-600">
                                            <Eye className="w-3 h-3" />
                                            {formatDistanceToNow(new Date(lastViewedAt), { addSuffix: true })}
                                        </span>
                                    )}
                                </div>
                                {/* Last responder info */}
                                {lastMessage && lastSender && (
                                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border/50">
                                        <Avatar className="w-6 h-6">
                                            {lastSender.profilePicture ? (
                                                <AvatarImage src={lastSender.profilePicture} alt={lastSender.firstName} />
                                            ) : null}
                                            <AvatarFallback className={isStaff ? "bg-blue-100 text-blue-700 text-xs" : "bg-gray-100 text-gray-700 text-xs"}>
                                                {isStaff ? (
                                                    <Headphones className="w-3 h-3" />
                                                ) : (
                                                    getInitials(lastSender.firstName, lastSender.lastName)
                                                )}
                                            </AvatarFallback>
                                        </Avatar>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs text-muted-foreground truncate">
                                                <span className="font-medium text-foreground">
                                                    {isStaff ? t("support.tickets.supportTeam") : `${lastSender.firstName || ""} ${lastSender.lastName || ""}`.trim()}
                                                </span>
                                                {" "}{t("support.tickets.replied")}{" "}
                                                {formatDistanceToNow(new Date(lastMessage.createdAt), { addSuffix: true })}
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-foreground transition-colors flex-shrink-0 mt-1" />
                        </div>
                    </Card>
                );
            })}
        </div>
    );
}
